import axiosInstance from '@/api/axiosInstance'
import { Button } from '@/components/ui/button'
import { AuthContext } from '@/contexts/auth-context'
import { useQuery } from '@tanstack/react-query'
import { useContext, useEffect, useState } from 'react'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'

function RewardHistory() {
  const { auth } = useContext(AuthContext)
  const { id: businessId } = useParams()
  const { state } = useLocation()
  const navigate = useNavigate()

  const businessName = state?.businessDetails?.business_name || null

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    totalRecords: 0,
    totalPages: 1,
  })

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['rewardHistoryData', pagination.page, businessId],
    queryFn: async () => {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
      }

      if (businessId && businessId !== 'all') {
        params.businessId = businessId
      }

      const response = await axiosInstance.get('/web-api/rewards/history', {
        params,
        headers: {
          Authorization: `Bearer ${auth.token}`,
        },
      })
      return response.data
    },
  })

  useEffect(() => {
    if (data) {
      setPagination((prev) => ({
        ...prev,
        totalRecords: data.pagination.totalRecords,
        totalPages: data.pagination.totalPages,
      }))
    }
  }, [data])
  
  useEffect(() => {
    window.scrollTo(0, 0) 
  }, [])

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }))
  }

  const getImageUrl = (path) => {
    return path ? `${import.meta.env.VITE_BASE_URL}${path}` : '/customer-assets/BLogo.svg'
  }

  return (
    <div className="container flex flex-col gap-4 p-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="p-0 h-8 w-8">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h3 className="text-lg font-semibold text-gray-900">
          Reward History {businessName ? ` - ${businessName}` : ''}
        </h3>
      </div>

      {isLoading ? (
        <div className="animate-pulse space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-24 bg-gray-100 rounded-lg"></div>
          ))}
        </div>
      ) : error ? (
        <div className="text-center p-4 border border-red-200 rounded bg-red-50">
          <p className="text-red-600">Failed to load reward history</p>
          <Button onClick={refetch} size="sm" className="mt-2">
            Retry
          </Button>
        </div>
      ) : data?.data?.length === 0 ? (
        <div className="text-center p-4 border rounded bg-gray-50">
          <p className="text-gray-600">No reward history found</p>
        </div>
      ) : (
        <>
          <div className="space-y-4">
            {data?.data?.map((reward) => (
              <div
                key={reward._id}
                className="border border-purple-300 bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow italic"
              >
                <p className="bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-transparent font-medium mb-3">
                  {reward.description.split(/(\d+)/).map((part, index) =>
                    /\d+/.test(part) ? (
                      <span key={index} className=" text-yellow-500 underline ">
                        {part}
                      </span>
                    ) : (
                      <span key={index}>{part}</span>
                    )
                  )}
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {reward.storeLogo && (
                      <div className="w-6 h-6 rounded-full overflow-hidden">
                        <img
                          src={getImageUrl(reward.storeLogo)}
                          alt="store logo"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <span className="text-sm text-gray-600">{reward.storeName}</span>
                  </div>
                  <span className="text-sm text-gray-500">{reward.date}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-gray-600">
              Page {pagination.page} of {pagination.totalPages}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1 || isLoading}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages || isLoading}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default RewardHistory
